import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, Plus, Upload, FileText } from "lucide-react";
import { adminSettings, adminUploads, formatError } from "../api";
import { Button, Card, Input, PageHeader, Spinner, Switch, Textarea } from "../ui";

const TABS = ["General", "Contact", "Socials", "Logos", "Cookie Banner", "Profile PDF"];

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("General");
  const [busy, setBusy] = useState(false);

  useEffect(() => { adminSettings.get().then(setS).catch(() => setS(false)); }, []);
  if (s === false) return <p className="text-sm text-red-600">Site settings not loaded.</p>;
  if (!s) return <Spinner />;

  const set = (patch) => setS({ ...s, ...patch });
  const setContact = (patch) => setS({ ...s, contact: { ...(s.contact || {}), ...patch } });
  const setSocials = (patch) => setS({ ...s, socials: { ...(s.socials || {}), ...patch } });
  const setCookie = (patch) => setS({ ...s, cookie_banner: { ...(s.cookie_banner || {}), ...patch } });

  const save = async () => {
    setBusy(true);
    try {
      const updated = await adminSettings.put({
        site_title: s.site_title,
        org_short: s.org_short,
        org_full: s.org_full,
        contact: s.contact,
        socials: s.socials,
        primary_logo: s.primary_logo,
        secondary_logo: s.secondary_logo,
        header_mark: s.header_mark,
        footer_mark: s.footer_mark,
        alt_logos: s.alt_logos,
        favicon: s.favicon,
        cookie_banner: s.cookie_banner,
      });
      setS(updated);
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <PageHeader
        title="Site Settings"
        description="Globals applied across the public site."
        actions={<Button onClick={save} loading={busy} testId="settings-save"><Save className="h-3.5 w-3.5" /> Save changes</Button>}
      />

      <div className="border-b border-slate-200 mb-6 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`settings-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t ? "border-[hsl(var(--aoa-royal))] text-[hsl(var(--aoa-royal))]" : "border-transparent text-slate-500 hover:text-[hsl(var(--aoa-navy))]"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "General" && (
        <Card className="p-6 space-y-4 max-w-3xl">
          <Input label="Site title (browser tab)" value={s.site_title || ""} onChange={(e) => set({ site_title: e.target.value })} testId="settings-site-title" />
          <Input label="Organization short name" value={s.org_short || ""} onChange={(e) => set({ org_short: e.target.value })} />
          <Input label="Organization full name" value={s.org_full || ""} onChange={(e) => set({ org_full: e.target.value })} />
        </Card>
      )}

      {tab === "Contact" && (
        <Card className="p-6 space-y-4 max-w-3xl">
          <Textarea label="Address" rows={2} value={s.contact?.address || ""} onChange={(e) => setContact({ address: e.target.value })} testId="settings-contact-address" />
          <Input label="Email" type="email" value={s.contact?.email || ""} onChange={(e) => setContact({ email: e.target.value })} testId="settings-contact-email" />
          <div>
            <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">Phones</span>
            <div className="space-y-2">
              {(s.contact?.phones || []).map((p, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={p} onChange={(e) => { const next = [...s.contact.phones]; next[i] = e.target.value; setContact({ phones: next }); }} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`settings-phone-${i}`} />
                  <button onClick={() => setContact({ phones: s.contact.phones.filter((_, j) => j !== i) })} className="rounded-lg border border-slate-200 px-3 hover:border-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={() => setContact({ phones: [...(s.contact?.phones || []), ""] })} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[hsl(var(--aoa-royal))]"><Plus className="h-3.5 w-3.5" /> Add phone</button>
            </div>
          </div>
          <Input label="Working hours" value={s.contact?.working_hours || ""} onChange={(e) => setContact({ working_hours: e.target.value })} hint="Leave empty to hide on the public page." />
          <Input label="Google Maps embed URL" value={s.contact?.map_embed || ""} onChange={(e) => setContact({ map_embed: e.target.value })} />
        </Card>
      )}

      {tab === "Socials" && (
        <Card className="p-6 space-y-4 max-w-3xl">
          <p className="text-xs text-slate-500">Leave a field empty to hide that icon on the public site.</p>
          {["facebook", "twitter", "linkedin", "youtube", "instagram"].map((k) => (
            <Input
              key={k}
              label={k.charAt(0).toUpperCase() + k.slice(1)}
              value={s.socials?.[k] || ""}
              onChange={(e) => setSocials({ [k]: e.target.value })}
              placeholder="https://..."
              testId={`settings-social-${k}`}
            />
          ))}
        </Card>
      )}

      {tab === "Logos" && (
        <Card className="p-6 space-y-6 max-w-4xl">
          <LogoSlot label="Header mark (compact, navy)" value={s.header_mark} purpose="logo" onChange={(v) => set({ header_mark: v })} testId="header-mark" />
          <LogoSlot label="Footer mark (white, dark bg)" value={s.footer_mark} purpose="logo" onChange={(v) => set({ footer_mark: v })} testId="footer-mark" />
          <LogoSlot label="Primary logo (legacy / lockup)" value={s.primary_logo} purpose="logo" onChange={(v) => set({ primary_logo: v })} testId="primary" />
          <LogoSlot label="Secondary logo (legacy / lockup, white)" value={s.secondary_logo} purpose="logo" onChange={(v) => set({ secondary_logo: v })} testId="secondary" />
          <LogoSlot label="Favicon" value={s.favicon} purpose="favicon" onChange={(v) => set({ favicon: v })} testId="favicon" />
          <AltLogosEditor value={s.alt_logos || []} onChange={(v) => set({ alt_logos: v })} />
        </Card>
      )}

      {tab === "Cookie Banner" && (
        <Card className="p-6 space-y-4 max-w-3xl" data-testid="cookie-banner-tab">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-[hsl(var(--aoa-navy))]">Show banner on first visit</div>
              <p className="text-xs text-slate-500">When disabled, the banner never appears.</p>
            </div>
            <Switch checked={s.cookie_banner?.enabled !== false} onChange={(v) => setCookie({ enabled: v })} testId="cookie-enabled-toggle" />
          </div>
          <Textarea label="Body text" rows={4} value={s.cookie_banner?.body || ""} onChange={(e) => setCookie({ body: e.target.value })} testId="cookie-body" />
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Accept all label" value={s.cookie_banner?.accept_all_label || ""} onChange={(e) => setCookie({ accept_all_label: e.target.value })} testId="cookie-accept-label" />
            <Input label="Essential only label" value={s.cookie_banner?.essential_label || ""} onChange={(e) => setCookie({ essential_label: e.target.value })} testId="cookie-essential-label" />
            <Input label="Learn more label" value={s.cookie_banner?.learn_more_label || ""} onChange={(e) => setCookie({ learn_more_label: e.target.value })} testId="cookie-learn-label" />
            <Input label="Learn more URL" value={s.cookie_banner?.learn_more_url || ""} onChange={(e) => setCookie({ learn_more_url: e.target.value })} placeholder="/cookie-policy" testId="cookie-learn-url" />
          </div>
        </Card>
      )}

      {tab === "Profile PDF" && (
        <Card className="p-6 max-w-2xl">
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            <strong>Profile PDF download is currently hidden from the public site.</strong> The file remains served at <code>/api/files/profile</code> and can be re-linked from the website later.
          </div>
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-lg bg-[hsl(var(--aoa-royal))]/10">
              <FileText className="h-5 w-5 text-[hsl(var(--aoa-royal))]" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-[hsl(var(--aoa-navy))]">AOA Somalia Institutional Profile</div>
              <div className="text-xs text-slate-500 break-all">{s.profile_pdf_url}</div>
              <a href="/api/files/profile" target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs font-semibold text-[hsl(var(--aoa-royal))] hover:underline">Open current PDF →</a>
            </div>
          </div>
          <ProfilePdfUpload onUploaded={(url) => set({ profile_pdf_url: url })} />
          <p className="mt-4 text-xs text-slate-500">Uploading replaces the file served at <code>/api/files/profile</code> immediately.</p>
        </Card>
      )}
    </div>
  );
}

function LogoSlot({ label, value, purpose, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const r = await adminUploads.upload(f, purpose);
      onChange(r.url);
      toast.success("Logo uploaded.");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex flex-col md:flex-row gap-5 items-start" data-testid={`logo-slot-${testId}`}>
      <div className="grid h-24 w-24 place-items-center rounded-xl border border-slate-200 bg-slate-50 flex-none">
        {value ? <img src={value} alt="" className="max-h-20 max-w-20 object-contain" /> : <span className="text-xs text-slate-400">No image</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">{label}</div>
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`logo-url-${testId}`} />
        <label className="mt-2 inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 cursor-pointer hover:border-[hsl(var(--aoa-royal))]">
          <Upload className="h-3.5 w-3.5" />{busy ? "Uploading..." : "Upload"}
          <input type="file" hidden accept="image/*" onChange={handle} data-testid={`logo-file-${testId}`} />
        </label>
      </div>
    </div>
  );
}

function AltLogosEditor({ value, onChange }) {
  return (
    <div data-testid="alt-logos-editor">
      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-700 mb-2">Alternate logos (marketing variants)</div>
      <div className="space-y-2">
        {value.map((url, i) => (
          <div key={i} className="flex gap-2">
            <input type="text" value={url} onChange={(e) => { const next = [...value]; next[i] = e.target.value; onChange(next); }} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="rounded-lg border border-slate-200 px-3 hover:border-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button onClick={() => onChange([...value, ""])} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[hsl(var(--aoa-royal))]"><Plus className="h-3.5 w-3.5" /> Add variant</button>
      </div>
    </div>
  );
}

function ProfilePdfUpload({ onUploaded }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const r = await adminUploads.upload(f, "profile_pdf");
      onUploaded(r.url);
      toast.success("Profile PDF replaced.");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="mt-4 inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 cursor-pointer hover:border-[hsl(var(--aoa-royal))]">
      <Upload className="h-3.5 w-3.5" />
      {busy ? "Uploading..." : "Replace profile PDF"}
      <input type="file" hidden accept=".pdf" onChange={handle} data-testid="profile-pdf-upload" />
    </label>
  );
}
